import React, {useState, useEffect} from 'react';

import {sliceString, addHttp, replaceWithHttps, uploadImage} from 'utils';

import {
  PrimaryButton,
  SecondaryButton,
  IconButton,
  ConfirmMessage,
  ButtonGroup,
  ErrorMessage,
} from 'components';

import {ComponentType, Choice, Component} from '../types';
import {COMPONENT_TYPES} from '../constants';

import {componentName, validateComponent} from '../utils';

import TextForm from './components/TextForm';
import LinkForm from './components/LinkForm';
import VideoForm from './components/VideoForm';
import ImageForm from './components/ImageForm';
import MultipleChoiceForm from './components/MultipleChoiceForm';
import OpenQuestionForm from './components/OpenQuestionForm';
import MeetingForm from './components/MeetingForm';

import i18n from 'i18next';

const initialChoices = [{key: 0, text: '', isAnswer: false}];

// Describes a single change, that can be undo'd
enum ChangeType {
  'image',
  'value',
  'dateValue',
  'choices',
  'feedback',
  'caption',
}

class Change {
  type: ChangeType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  oldValue: any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(changeType: ChangeType, oldValue: any) {
    this.type = changeType;
    this.oldValue = oldValue;
  }
}

interface ComponentFormProps {
  type: ComponentType;
  onCancel: () => void;
  onGoBack: () => void;
  onConfirm: (newComponent: Partial<Component>) => void;
  onEdit: (selectedComponent: Component) => void;
  onDelete: (selectedId: string) => void;
  disabled?: boolean;
  component?: Component;
}

export default function ComponentForm(props: ComponentFormProps) {
  // Local state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Questionnable state:
  const [value, setValue] = useState<string>('');
  const [dateValue, setDateValue] = useState<Date>(new Date());
  const [uploadedImageFile, setUploadedImageFile] = useState<File | undefined>(
    undefined
  );
  const [choices, setChoices] = useState<Choice[]>(initialChoices);
  const [feedback, setFeedback] = useState<string>('');
  const [caption, setCaption] = useState<string>('');

  const [lastUpdate, setLastUpdate] = useState<Change | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    const {
      value: currValue = '',
      dateValue: currDateValue = new Date(),
      choices: currChoices,
      feedback: currFeedback = '',
      caption: currCaption = '',
    } = props.component || {};

    setValue(currValue);
    setDateValue(currDateValue);
    setChoices(currChoices || initialChoices);
    setFeedback(currFeedback);
    setCaption(currCaption);
    setUploadedImageFile(undefined);

    setLastUpdate(undefined);
    setErrorMessage(undefined);
  }, [props.type, props.component]);

  const handleSubmit = async () => {
    setErrorMessage(undefined);

    let updatedValue = value;
    if (props.type === COMPONENT_TYPES.LINK) {
      updatedValue = addHttp(updatedValue);
    } else if (props.type === COMPONENT_TYPES.VIDEO) {
      updatedValue = addHttp(updatedValue);
      updatedValue = replaceWithHttps(updatedValue);
    }

    const newComponent: Partial<Component> = {
      type: props.type,
      value: updatedValue,
      dateValue,
      imageId: uploadedImageFile ? 'to-be-uploaded' : props.component?.imageId,
      choices,
      feedback,
      caption,
    };

    let message = undefined;

    validateComponent(newComponent, () => {
      message = i18n.t(
        'wisbitPage.addComponentModal.validationErrors.addValidComponent',
        {component: componentName(props.type)}
      );
    });

    if (message) {
      setErrorMessage(message);
      return;
    }

    // Upload image so we get an image id to save in the wisbit
    if (uploadedImageFile) {
      newComponent.imageId = await uploadImage(uploadedImageFile);
      setUploadedImageFile(undefined);
    }

    if (props.component) {
      props.onEdit({
        ...props.component,
        ...newComponent,
      });
    } else {
      props.onConfirm(newComponent);
    }
  };

  const onUpdateValue = (newValue: string) => {
    setLastUpdate(new Change(ChangeType.value, sliceString(value)));
    setValue(newValue);
    setErrorMessage(undefined);
  };

  const onUpdateDateValue = (newValue: Date) => {
    setLastUpdate(new Change(ChangeType.dateValue, dateValue));
    setDateValue(newValue);
    setErrorMessage(undefined);
  };

  const onUpdateFile = async (newFile: File) => {
    setLastUpdate(new Change(ChangeType.image, uploadedImageFile));
    setUploadedImageFile(newFile);
    setErrorMessage(undefined);
  };

  const onUpdateChoices = (newChoices: Choice[]) => {
    setLastUpdate(
      new Change(
        ChangeType.choices,
        choices.length === newChoices.length
          ? choices.map((choice, index) => {
              const {text, isAnswer} = choice;
              const {text: newText, isAnswer: newIsAnswer} = newChoices[index];

              if (text === newText && isAnswer === newIsAnswer) {
                return choice;
              } else if (isAnswer === newIsAnswer) {
                return {
                  ...choice,
                  text: sliceString(text),
                };
              } else {
                return {
                  ...choice,
                  isAnswer,
                };
              }
            })
          : choices
      )
    );

    setChoices(newChoices);
    setErrorMessage(undefined);
  };

  const onUpdateFeedback = (newFeedback: string) => {
    setLastUpdate(new Change(ChangeType.feedback, sliceString(feedback)));
    setFeedback(newFeedback);
    setErrorMessage(undefined);
  };

  const onUpdateCaption = (newCaption: string) => {
    setLastUpdate(new Change(ChangeType.caption, sliceString(caption)));
    setCaption(newCaption);
    setErrorMessage(undefined);
  };

  const onUndo = () => {
    if (!lastUpdate) {
      return;
    }

    switch (lastUpdate.type) {
      case ChangeType.caption:
        setCaption(lastUpdate.oldValue);
        break;
      case ChangeType.feedback:
        setFeedback(lastUpdate.oldValue);
        break;
      case ChangeType.choices:
        setChoices(lastUpdate.oldValue);
        break;
      case ChangeType.image:
        setUploadedImageFile(lastUpdate.oldValue);
        break;
      case ChangeType.dateValue:
        setDateValue(lastUpdate.oldValue);
        break;
      case ChangeType.value:
        setValue(lastUpdate.oldValue);
        break;
    }
    setLastUpdate(undefined);
    setErrorMessage(undefined);
  };

  const commonProps = {
    value,
    setValue: onUpdateValue,
    disabled: props.disabled,
  };

  return (
    <>
      {props.type === COMPONENT_TYPES.TEXT && <TextForm {...commonProps} />}
      {props.type === COMPONENT_TYPES.LINK && (
        <LinkForm
          {...commonProps}
          caption={caption}
          setCaption={onUpdateCaption}
        />
      )}
      {props.type === COMPONENT_TYPES.VIDEO && <VideoForm {...commonProps} />}
      {props.type === COMPONENT_TYPES.MEETING && (
        <MeetingForm
          dateValue={dateValue}
          setDateValue={onUpdateDateValue}
          disabled={props.disabled}
        />
      )}
      {props.type === COMPONENT_TYPES.IMAGE && (
        <ImageForm
          imageId={props.component?.imageId}
          uploadedFile={uploadedImageFile}
          setFile={onUpdateFile}
          disabled={props.disabled}
        />
      )}
      {props.type === COMPONENT_TYPES.MULTIPLE_CHOICES && (
        <MultipleChoiceForm
          title={value}
          setTitle={onUpdateValue}
          choices={choices}
          setChoices={onUpdateChoices}
          feedback={feedback}
          setFeedback={onUpdateFeedback}
          disabled={props.disabled}
          isEdit={!!props.component}
        />
      )}
      {props.type === COMPONENT_TYPES.OPEN_QUESTION && (
        <OpenQuestionForm
          title={value}
          setTitle={onUpdateValue}
          feedback={feedback}
          setFeedback={onUpdateFeedback}
          disabled={props.disabled}
          isEdit={!!props.component}
        />
      )}
      {errorMessage && (
        <ErrorMessage className="my-4">{errorMessage}</ErrorMessage>
      )}
      <div className="flex mt-6 justify-between">
        <ButtonGroup className={props.disabled ? 'invisible' : ''}>
          {props.component && (
            <IconButton
              type="delete"
              onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
            />
          )}
          {props.component && showDeleteConfirm && (
            <ConfirmMessage
              className="h-36 w-72"
              cancelText={i18n.t('wisbitPage.addComponentModal.delete.cancel')}
              onCancel={() => setShowDeleteConfirm(false)}
              confirmText={i18n.t(
                'wisbitPage.addComponentModal.delete.confirm'
              )}
              onConfirm={() =>
                props.component && props.onDelete(props.component.id)
              }
            >
              {i18n.t('wisbitPage.addComponentModal.delete.prompt')}
            </ConfirmMessage>
          )}
          {lastUpdate && <IconButton type="undo" onClick={onUndo} />}
        </ButtonGroup>
        <div>
          <SecondaryButton className="h-14 w-28" onClick={props.onCancel}>
            {i18n.t('common.cancel')}
          </SecondaryButton>
          {!props.disabled && (
            <PrimaryButton className="h-14 w-28 ml-4" onClick={handleSubmit}>
              {i18n.t('wisbitPage.addComponentModal.save')}
            </PrimaryButton>
          )}
        </div>
      </div>
    </>
  );
}
